import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import Images from "../../utils/Images";
import { heroSlides } from "../../data/heroSlides";
import HeroBanner from "../../components/insurance/HeroBanner";
import PrimaryFilters from "../../components/insurance/PrimaryFilters";
import SecondaryFilters from "../../components/insurance/SecondaryFilters";
import InsuranceGrid from "../../components/insurance/InsuranceGrid";
import useInsuranceData from "../../components/insurance/useInsuranceData";
import PackageDrawer from "../../components/insurance/PackageDrawer";
import AdvancedSearchDrawer from "../../components/insurance/AdvancedSearchDrawer";
import policyService from "../../api/PolicyService";
import { toast } from "react-toastify";
import Loader from "../../components/misc/Loader";

// Mapping of filters to a specific hero slide index
const filterConfigs = [
  {
    key: "daily",
    title: "Daily Commuters",
    image: Images.daily_commuter,
    slideIndex: 0,
    isActive: true,
  },
  {
    key: "intercity",
    title: "Intercity Travel",
    image: Images.intercity_travel,
    slideIndex: 1,
    isActive: true,
  },
  {
    key: "bikers",
    title: "Motor Riders",
    image: Images.okada,
    slideIndex: 2,
    isActive: true,
  },
];

export default function InsuranceHero() {
  const navigate = useNavigate();
  const [selectedKey, setSelectedKey] = useState(filterConfigs[1].key);
  const [secondaryTab, setSecondaryTab] = useState("featured");
  const [currentPage, setCurrentPage] = useState(1);
  const [isPackageOpen, setIsPackageOpen] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [randomSlideIndex, setRandomSlideIndex] = useState(0);

  useEffect(() => {
    const fetchPolicies = async () => {
      setLoading(true);
      try {
        const response = await policyService.getPolicies();
        // Handle different response structures
        if (response.status && response.data) {
          // Check if data is an array or if it's nested
          const policiesData = Array.isArray(response.data) 
            ? response.data 
            : (response.data.data || response.data.policies || []);
          
          // Ensure it's an array before setting
          if (Array.isArray(policiesData)) {
            setPolicies(policiesData);
          } else {
            console.error("Policies data is not an array:", policiesData);
            setPolicies([]);
            toast.error("Invalid response format from server");
          }
        } else {
          setPolicies([]);
          toast.error(
            response.message || "An error occurred while fetching policies"
          );
        }
      } catch (error) {
        console.error("Error fetching policies:", error);
        setPolicies([]);
        toast.error(
          error.response?.data?.message ||
            "An error occurred while fetching policies"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchPolicies();
  }, []);

  // Use random slide index that was set on mount, cycling through available slides
  const currentSlide = heroSlides[randomSlideIndex] || heroSlides[0];

  useEffect(() => {
    window.scrollTo(0, 0);
    // Randomly select a hero slide on component mount/refresh
    const randomIndex = Math.floor(Math.random() * heroSlides.length);
    setRandomSlideIndex(randomIndex);
  }, []);

  // Reset page when filters change
  const resetPageOnFilterChange = (key, tab) => {
    setFilterLoading(true);
    setSelectedKey(key);
    if (typeof tab === "string") setSecondaryTab(tab);
    setCurrentPage(1);

    setTimeout(() => {
      setFilterLoading(false);
    }, 800);
  };

  // Use modular data hook with real policy data and advanced filters
  const allItems = useInsuranceData(
    selectedKey,
    secondaryTab,
    policies,
    advancedFilters
  );

  const ITEMS_PER_PAGE = 9;
  const totalPages = Math.max(1, Math.ceil(allItems.length / ITEMS_PER_PAGE));
  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return allItems.slice(start, start + ITEMS_PER_PAGE);
  }, [allItems, currentPage]);

  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  const handleCardClick = (item) => {
    setSelectedItem(item);
    setIsPackageOpen(true);
    setIsAdvancedOpen(false);
  };

  if (loading) {
    return <Loader fullScreen={true} text="Loading policies..." />;
  }

  return (
    <section className="overflow-hidden relative">
      {/* Hero */}
      <div className="overflow-hidden relative z-0 w-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={randomSlideIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <HeroBanner slide={currentSlide} />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Filters */}
      <div className="relative z-10 px-4 py-6 -mt-1 bg-gradient-to-b from-white to-gray-50 sm:py-8 md:py-10 lg:py-12 sm:-mt-2 md:-mt-3 lg:-mt-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex justify-between items-end mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">
                Select Your Cover
              </h3>
              <div className="mt-2 w-28 h-1 bg-emerald-500 rounded" />
            </div>
            <button
              onClick={() => navigate("/dashboard")}
              className="inline-flex gap-2 items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 shadow-sm hover:bg-gray-50"
            >
              <ChevronLeft size={16} /> Back to Dashboard
            </button>
          </div>

          <PrimaryFilters
            filters={filterConfigs}
            selectedKey={selectedKey}
            onSelect={(key) => resetPageOnFilterChange(key)}
            loading={filterLoading}
          />

          {/* Secondary Filters */}
          <div className="mt-8">
            <SecondaryFilters
              tab={secondaryTab}
              onTabChange={(key) => resetPageOnFilterChange(selectedKey, key)}
              disableOpen={isPackageOpen}
              open={isAdvancedOpen}
              onOpenChange={setIsAdvancedOpen}
              onApply={(filters) => {
                setAdvancedFilters(filters);
                setCurrentPage(1);
              }}
            />

            {/* Advanced Search Drawer */}
            <AdvancedSearchDrawer
              open={isAdvancedOpen}
              onClose={() => setIsAdvancedOpen(false)}
              onApply={(filters) => {
                setAdvancedFilters(filters);
                setCurrentPage(1);
              }}
            />

            {/* Results grid */}
            {loading || filterLoading ? (
              <div className="flex justify-center items-center py-12 mt-6">
                <div className="flex flex-col gap-4 items-center">
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full border-b-2 border-emerald-500 animate-spin"></div>
                    <div className="absolute inset-0 w-8 h-8 rounded-full border-2 border-emerald-200 animate-pulse"></div>
                  </div>
                  <p className="text-sm text-gray-600">
                    {filterLoading
                      ? "Loading fresh policies..."
                      : "Loading policies..."}
                  </p>
                </div>
              </div>
            ) : allItems.length === 0 ? (
              <div className="flex justify-center items-center py-12 mt-6">
                <div className="flex flex-col gap-4 items-center text-center">
                  <div className="flex justify-center items-center w-16 h-16 bg-gray-100 rounded-full">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      No policies found
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      No policies available for the selected category. Try
                      selecting a different filter.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <InsuranceGrid
                items={pageItems}
                currentPage={currentPage}
                total={allItems.length}
                perPage={ITEMS_PER_PAGE}
                onPrev={handlePrev}
                onNext={handleNext}
                onGo={(n) => setCurrentPage(n)}
                onCardClick={handleCardClick}
              />
            )}
          </div>
        </div>
      </div>
      <PackageDrawer
        open={isPackageOpen}
        onClose={() => setIsPackageOpen(false)}
        item={selectedItem}
      />
    </section>
  );
}

